import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, CheckCircle2, Circle, Flame, MessageCircle, BookOpen, Bookmark, Highlighter } from "lucide-react";
import { toast } from "sonner";

const CHAPTER_TITLES_STUB = {}; // filled from GET /api/course

export default function StudentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [answerDrafts, setAnswerDrafts] = useState({});

  const load = useCallback(() => {
    Promise.all([
      api.get(`/admin/students/${id}`),
      api.get("/course"),
    ]).then(([d, c]) => {
      setData(d.data);
      setChapters(c.data);
    }).catch(() => toast.error("Could not load student"));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const answer = async (qid) => {
    const a = (answerDrafts[qid] || "").trim();
    if (!a) return;
    try {
      await api.post(`/questions/${qid}/answer`, { answer: a });
      toast.success("Reply sent");
      setAnswerDrafts(d => ({ ...d, [qid]: "" }));
      load();
    } catch (e) { toast.error("Reply failed"); }
  };

  if (!data) return <Layout><div className="p-10">Loading…</div></Layout>;

  const { student, progress, exam, questions, highlights_count, bookmarks_count, days_active } = data;
  const progressMap = new Map(progress.map(p => [p.chapter_id, p]));
  const chaptersPassed = progress.filter(p => p.passed).length;
  const avgScore = progress.length ? Math.round(progress.reduce((s, p) => s + (p.score || 0), 0) / progress.length) : 0;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 lg:p-12">
        <Link to="/admin/students" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-students">
          <ArrowLeft size={14} /> Back to roster
        </Link>

        <header className="mt-6 mb-10 pb-8 border-b border-border">
          <div className="label-caps mb-3">Student profile</div>
          <h1 className="font-serif text-4xl lg:text-6xl leading-none" data-testid="student-detail-name">{student.name}</h1>
          <div className="text-muted-foreground mt-1">{student.email}</div>
          {student.created_at && <div className="text-xs text-muted-foreground mt-1">Joined {new Date(student.created_at).toLocaleDateString()}</div>}
        </header>

        <section className="grid md:grid-cols-4 gap-4 mb-10">
          <Card className="p-5">
            <div className="label-caps mb-1">Chapters</div>
            <div className="font-serif text-4xl leading-none">{chaptersPassed}<span className="text-xl text-muted-foreground">/13</span></div>
            <Progress value={(chaptersPassed / 13) * 100} className="mt-3 h-1" />
          </Card>
          <Card className="p-5">
            <div className="label-caps mb-1">Avg quiz</div>
            <div className="font-serif text-4xl leading-none">{avgScore}<span className="text-xl text-muted-foreground">%</span></div>
          </Card>
          <Card className="p-5">
            <div className="label-caps mb-1">Final exam</div>
            <div className="font-serif text-4xl leading-none flex items-center gap-2">
              {exam ? `${exam.score}%` : "—"}
              {exam?.passed && <CheckCircle2 size={20} className="text-primary" />}
            </div>
          </Card>
          <Card className="p-5">
            <div className="label-caps mb-1">Days active</div>
            <div className="font-serif text-4xl leading-none flex items-center gap-2">
              {days_active}
              <Flame size={18} className={days_active ? "text-accent" : "text-muted-foreground"} />
            </div>
            <div className="text-xs text-muted-foreground mt-1">{bookmarks_count} bookmarks · {highlights_count} highlights</div>
          </Card>
        </section>

        <section className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={18} className="text-primary" />
            <h2 className="font-serif text-2xl">Quiz history</h2>
          </div>
          <div className="space-y-2" data-testid="quiz-history-list">
            {chapters.map(ch => {
              const p = progressMap.get(ch.id);
              return (
                <Card key={ch.id} className="p-4 flex items-center gap-4" data-testid={`quiz-history-${ch.id}`}>
                  <div className="font-serif text-2xl w-10 text-muted-foreground shrink-0">{String(ch.id).padStart(2, "0")}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg leading-tight truncate">{ch.title}</div>
                    <div className="label-caps text-[10px] mt-0.5">{ch.category}</div>
                  </div>
                  <div className="w-32 shrink-0">
                    <Progress value={p?.score || 0} className="h-2" />
                    <div className="text-[10px] font-mono text-muted-foreground mt-1 text-right">{p?.score != null ? `${p.score}%` : "Not started"}</div>
                  </div>
                  {p?.passed ? <CheckCircle2 size={16} className="text-primary shrink-0" /> : <Circle size={16} className="text-muted-foreground shrink-0" />}
                </Card>
              );
            })}
          </div>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle size={18} className="text-accent" />
            <h2 className="font-serif text-2xl">Questions <span className="text-sm text-muted-foreground">({questions.length})</span></h2>
          </div>
          {questions.length === 0 ? (
            <Card className="p-6 text-sm text-muted-foreground">No questions from this student yet.</Card>
          ) : (
            <div className="space-y-3" data-testid="student-questions-list">
              {questions.map(q => (
                <Card key={q.id} className="p-5" data-testid={`student-detail-q-${q.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-caps flex items-center gap-2">
                      {q.status === "answered"
                        ? <><CheckCircle2 size={12} className="text-primary" /> Answered</>
                        : <><span className="h-2 w-2 rounded-full bg-accent" /> Awaiting reply</>}
                      {q.chapter_id && <span className="text-muted-foreground">· Ch {q.chapter_id}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleString()}</div>
                  </div>
                  <div className="whitespace-pre-wrap">{q.text}</div>
                  {q.answer ? (
                    <div className="mt-3 border-l-4 border-primary pl-4 py-1">
                      <div className="label-caps mb-1">Reply · {q.answered_by}</div>
                      <div className="whitespace-pre-wrap text-sm">{q.answer}</div>
                      {q.answered_by_signature && (
                        <div className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap italic">{q.answered_by_signature}</div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      <Textarea
                        data-testid={`detail-answer-input-${q.id}`}
                        value={answerDrafts[q.id] || ""}
                        onChange={e => setAnswerDrafts(d => ({ ...d, [q.id]: e.target.value }))}
                        placeholder="Reply to this student…"
                        rows={3}
                      />
                      <Button size="sm" onClick={() => answer(q.id)} data-testid={`detail-answer-send-${q.id}`}>Send reply</Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
